import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {NgSelectModule} from "@ng-select/ng-select";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {
    FormBuilder,
    FormControl,
    FormGroup, FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators
} from "@angular/forms";
import {Pagination, PaginationResponse} from "@models/pagination.model";
import {NgIf} from "@angular/common";
import {ToastrService} from "ngx-toastr";
import {CarerRelationshipsService} from "@shared/api/services/v2/library/carer-relationships.service";
import {CarerRelationship, Patient} from "@shared/api/models/query/patient.model";
import {PatientsService} from "@shared/api/services/v2/patients.service";
import {NgxIntlTelInputModule} from "ngx-intl-tel-input";
import parsePhoneNumberFromString from "libphonenumber-js";

import {UnmaskingStateService} from "@shared/api/services/v2/unmasking-state.service";
import {Subscription} from "rxjs";
import {calculateAge} from "@shared/utils/calculate-age";
import {BatchRevealButtonComponent} from "@shared/components/masked-field/batch-reveal-button.component";
import {SensitiveDataMaskingConfig} from "@shared/config/sensitive-data-masking.config";

@Component({
    selector: 'patient-registration-personal-info',
    imports: [
        SpinnerComponent,
        ReactiveFormsModule,
        NgIf,
        NgxIntlTelInputModule,
        FormsModule,
        BatchRevealButtonComponent,
        NgSelectModule,
    ],
    standalone: true,
    templateUrl: './patient-registration-personal-info.component.html'
})
export class PatientRegistrationPersonalInfoComponent implements OnInit, OnDestroy {
    @Output() output = new EventEmitter<{patientId: number, draft: boolean}>();

    loading: boolean = false
    pagination: Pagination = new Pagination()
    typeOfUser: string
    departmentId: number
    licenseId: number
    clinicianId: number | null = null
    isTest: boolean = false
    patient?: Patient
    shownCarerRelationshipNotes: boolean = false

    carerRelationshipList: CarerRelationship[] = []

    patientGroup: FormGroup
    carerGroup: FormGroup
    form: FormGroup = new FormGroup({})

    private unmaskingSubscription: Subscription;
    public isUnmasked: boolean = false;
    public isCarerUnmasked: boolean = false;

    constructor(
        private formBuilder: FormBuilder,
        private toastr: ToastrService,
        private carerRelationshipsService: CarerRelationshipsService,
        private patientsService: PatientsService,
        public unmaskingStateService: UnmaskingStateService,
        private readonly maskingConfig: SensitiveDataMaskingConfig,
    ) {
    }

    ngOnInit(): void {
        this.initForms()

        this.pagination.size = 999
        this.pagination.filter = {
            scope: "cdss,both"
        }
        this.carerGroup.get("carerRelationship").disable()
        this.carerRelationshipsService.listPaginated(this.pagination).subscribe({
            next: (response: PaginationResponse<CarerRelationship>) => {
                if (!response?.data?.length) {
                    response.data = []
                }
                this.carerRelationshipList = response.data;
                this.carerGroup.get("carerRelationship").enable()
            }
        })

        this.unmaskingSubscription = this.unmaskingStateService.getUnmaskedState().subscribe(state => {
            if (!this.maskingConfig.enabled) {
                // UNIVAPLAT-2790: masking disabled — data arrives in clear and all controls stay
                // enabled; reveal-state changes must never re-patch or re-disable anything.
                return;
            }

            if (this.patient?.user?.id) {
                const userState = state.find(item => item.userId.toString() === this.patient.user.id.toString());
                this.isUnmasked = !!userState;
                const patientControls = ['firstName', 'lastName', 'email', 'phoneNumber', 'nhsNumber', 'nhsNumber2'];

                if (userState) {
                    const data = userState.data;
                    const patch: any = {};
                    if (data.firstName) patch.firstName = data.firstName;
                    if (data.lastName) patch.lastName = data.lastName;
                    if (data.email) patch.email = data.email;
                    if (data.nhsNumber) {
                        patch.nhsNumber = data.nhsNumber;
                        patch.nhsNumber2 = data.nhsNumber;
                    }
                    if (data.phoneNumber) {
                        const phoneNumber = parsePhoneNumberFromString(data.phoneNumber as string);
                        if (phoneNumber) {
                            patch.phoneNumber = {
                                number: phoneNumber.nationalNumber,
                                countryCode: phoneNumber.country,
                                dialCode: '+' + phoneNumber.countryCallingCode,
                            };
                        }
                    }
                    this.patientGroup.patchValue(patch);
                    patientControls.forEach(control => {
                        const c = this.patientGroup.get(control);
                        if (c) {
                            c.enable();
                        }
                    });
                } else {
                    // Re-mask data if it was unmasked before
                    this.patientGroup.patchValue({
                        firstName: this.patient.user.firstName,
                        lastName: this.patient.user.lastName,
                        email: this.patient.user.email,
                        phoneNumber: this.patient.user.phoneNumber,
                        nhsNumber: this.patient.nhsNumber,
                        nhsNumber2: this.patient.nhsNumber,
                    });
                    patientControls.forEach(control => {
                        const c = this.patientGroup.get(control);
                        if (c) {
                            c.disable();
                        }
                    });
                }
            }

            if (this.patient?.carer?.user?.id) {
                const carerUserState = state.find(item => item.userId.toString() === this.patient.carer.user.id.toString());
                this.isCarerUnmasked = !!carerUserState;
                const carerControls = ['carerFirstName', 'carerLastName', 'carerEmail', 'carerPhoneNumber'];

                if (carerUserState) {
                    const data = carerUserState.data;
                    const patch: any = {};
                    if (data.firstName) patch.carerFirstName = data.firstName;
                    if (data.lastName) patch.carerLastName = data.lastName;
                    if (data.email) patch.carerEmail = data.email;
                    if (data.phoneNumber) {
                        const patientPhoneNumber = parsePhoneNumberFromString(data.phoneNumber as string);
                        const phoneNumber = patientPhoneNumber ? {number: '0' + patientPhoneNumber.nationalNumber, countryCode: patientPhoneNumber.country} : data.phoneNumber;
                        if (phoneNumber) {
                            patch.carerPhoneNumber = phoneNumber
                        }
                    }
                    this.carerGroup.patchValue(patch);
                    carerControls.forEach(control => {
                        const c = this.carerGroup.get(control);
                        if (c) {
                            c.enable();
                        }
                    });
                } else {
                    // Re-mask data if it was unmasked before
                    this.carerGroup.patchValue({
                        carerFirstName: this.patient.carer.user.firstName,
                        carerLastName: this.patient.carer.user.lastName,
                        carerEmail: this.patient.carer.user.email,
                        carerPhoneNumber: this.patient.carer.user.phoneNumber
                    });
                    carerControls.forEach(control => {
                        const c = this.carerGroup.get(control);
                        if (c) {
                            c.disable();
                        }
                    });
                }
            }
        });
    }

    ngOnDestroy() {
        if (this.unmaskingSubscription) {
            this.unmaskingSubscription.unsubscribe();
        }
    }

    init(typeOfUser: string, departmentId: number, licenseId: number, clinicianId: number | null, isTest: boolean, patient?: Patient) {
        this.typeOfUser = typeOfUser
        this.departmentId = departmentId
        this.licenseId = licenseId
        this.clinicianId = clinicianId
        this.isTest = isTest === true
        this.patient = patient

        const maskingEnabled = this.maskingConfig.enabled;
        if (!maskingEnabled) {
            // UNIVAPLAT-2790: masking disabled — patient and carer are permanently unmasked,
            // so updatePatient()'s isPatient/isCarer gating always evaluates truthy and the
            // template conditionals resolve to the editable branch.
            this.isUnmasked = true;
            this.isCarerUnmasked = true;
        }

        switch (typeOfUser) {
            case "Patient":
                this.form = this.formBuilder.group({
                    patient: this.patientGroup,
                })
                break
            case "Carer":
                this.form = this.formBuilder.group({
                    carer: this.carerGroup,
                })
                break
            case "Patient and carer":
                this.form = this.formBuilder.group({
                    patient: this.patientGroup,
                    carer: this.carerGroup,
                })
        }

        if (patient) {
            // With masking disabled the API already returns clear values, so the unmasking
            // store is never consulted; with masking enabled, behaviour is unchanged.
            const useUnmaskedStore = maskingEnabled && this.unmaskingStateService.isUserUnmasked(patient.user.id);
            this.isUnmasked = !maskingEnabled || useUnmaskedStore;
            const patientPhoneNumberRaw = useUnmaskedStore ? this.unmaskingStateService.getUnmaskedValue(patient.user.id, 'phoneNumber') : patient.user.phoneNumber;
            const patientPhoneNumber = patientPhoneNumberRaw == null ? null : parsePhoneNumberFromString(patientPhoneNumberRaw);
            const patientEmail = useUnmaskedStore ? this.unmaskingStateService.getUnmaskedValue(patient.user.id, 'email') : patient.user.email;

            this.patientGroup.patchValue({
                firstName: useUnmaskedStore ? this.unmaskingStateService.getUnmaskedValue(patient.user.id, 'firstName') : patient.user.firstName,
                lastName: useUnmaskedStore ? this.unmaskingStateService.getUnmaskedValue(patient.user.id, 'lastName') : patient.user.lastName,
                dateOfBirth: patient.dateOfBirth,
                age: patient.age,
                sexAtBirth: patient.biologicalSex,
                phoneNumber: this.isUnmasked ? patientPhoneNumber ? {number: '0' + patientPhoneNumber.nationalNumber, countryCode: patientPhoneNumber.country} : null : patient.user.phoneNumber,
                email: patientEmail,
                patientEmailUnknown: patientEmail == null || patientEmail == "",
                nhsNumber: useUnmaskedStore ? this.unmaskingStateService.getUnmaskedValue(patient.user.id, 'nhsNumber') : patient.nhsNumber,
                nhsNumber2: useUnmaskedStore ? this.unmaskingStateService.getUnmaskedValue(patient.user.id, 'nhsNumber') : patient.nhsNumber,
                weight: patient.weight,
                height: patient.height,
            });

            const patientControls = ['firstName', 'lastName', 'email', 'phoneNumber', 'nhsNumber', 'nhsNumber2'];
            if (this.isUnmasked || !patient.user?.id) {
                patientControls.forEach(control => this.patientGroup.get(control)?.enable());
            } else {
                patientControls.forEach(control => this.patientGroup.get(control)?.disable());
            }

            if (patient.carer?.user) {
                const useCarerUnmaskedStore = maskingEnabled && this.unmaskingStateService.isUserUnmasked(patient.carer.user.id);
                this.isCarerUnmasked = !maskingEnabled || useCarerUnmaskedStore;
                const carerPhoneNumberRaw = useCarerUnmaskedStore ? this.unmaskingStateService.getUnmaskedValue(patient.carer.user.id, 'phoneNumber') : patient.carer.user.phoneNumber;
                const carerPhoneNumber = carerPhoneNumberRaw == null ? null : parsePhoneNumberFromString(carerPhoneNumberRaw);
                const carerEmail = useCarerUnmaskedStore ? this.unmaskingStateService.getUnmaskedValue(patient.carer.user.id, 'email') : patient.carer.user.email;

                this.carerGroup.patchValue({
                    carerFirstName: useCarerUnmaskedStore ? this.unmaskingStateService.getUnmaskedValue(patient.carer.user.id, 'firstName') : patient.carer.user.firstName,
                    carerLastName: useCarerUnmaskedStore ? this.unmaskingStateService.getUnmaskedValue(patient.carer.user.id, 'lastName') : patient.carer.user.lastName,
                    carerPhoneNumber: this.isCarerUnmasked ? carerPhoneNumber ? {number: '0' + carerPhoneNumber.nationalNumber, countryCode: carerPhoneNumber.country} : null : patient.carer.user.phoneNumber,
                    carerEmail: carerEmail,
                    carerEmailUnknown: carerEmail == null || carerEmail == "",
                    carerRelationship: patient.carerRelationship?.id,
                    carerRelationshipNotes: patient.carerRelationshipNotes,
                });

                const carerControls = ['carerFirstName', 'carerLastName', 'carerEmail', 'carerPhoneNumber'];
                if (this.isCarerUnmasked || !patient.carer?.user?.id) {
                    carerControls.forEach(control => this.carerGroup.get(control)?.enable());
                } else {
                    carerControls.forEach(control => this.carerGroup.get(control)?.disable());
                }
            }

            this.patientEmailUnknownChange()
            this.carerEmailUnknownChange()

            // The *EmailUnknownChange() calls above re-enable the email control whenever an email
            // exists; a masked user's email must stay read-only.
            if (!this.isUnmasked && patient.user?.id) {
                this.patientGroup.get('email')?.disable()
            }
            if (patient.carer?.user?.id && !this.isCarerUnmasked) {
                this.carerGroup.get('carerEmail')?.disable()
            }
        }

        // Trigger the unmasking logic with current state
        this.unmaskingStateService.resetTimer(patient?.user?.id);
        if (patient?.carer?.user?.id) {
            this.unmaskingStateService.resetTimer(patient.carer.user.id);
        }

    }

    initForms() {
        this.patientGroup = this.formBuilder.group({
            firstName: this.formBuilder.control<string>(null, Validators.required),
            lastName: this.formBuilder.control<string>(null, Validators.required),
            dateOfBirth: this.formBuilder.control<string>(null, Validators.required),
            age: this.formBuilder.control<number>(null, Validators.required),
            sexAtBirth: this.formBuilder.control<string>(null, Validators.required),
            phoneNumber: this.formBuilder.control<string>(null, Validators.required),
            email: this.formBuilder.control<string>(null, [Validators.email, this.validatePatientEmail()]),
            patientEmailUnknown: this.formBuilder.control<boolean>(false),
            nhsNumber: this.formBuilder.control<string>(null),
            nhsNumber2: this.formBuilder.control<string>(null),
            weight: this.formBuilder.control<number>(0, Validators.required),
            height: this.formBuilder.control<number>(0, Validators.required),
        }, {
            validators: [
                (control) => {
                    if (control.get("nhsNumber").value != control.get("nhsNumber2").value) {
                        control.get("nhsNumber2").setErrors({...(control.errors || {}), match: "Value does not match"})
                    }
                    return null
                }
            ]
        });
        this.patientGroup.get('dateOfBirth')?.valueChanges.subscribe(value => {
            const age = this.calculateAge(value);
            this.patientGroup.get('age')?.setValue(age, { emitEvent: false });
        });

        this.carerGroup = this.formBuilder.group({
            carerFirstName: this.formBuilder.control<string>(null, Validators.required),
            carerLastName: this.formBuilder.control<string>(null, Validators.required),
            carerPhoneNumber: this.formBuilder.control<string>(null, Validators.required),
            carerEmail: this.formBuilder.control<string>(null, [Validators.email, this.validateCarerEmail()]),
            carerEmailUnknown: this.formBuilder.control<boolean>(false),
            carerRelationship: this.formBuilder.control<number>(null, Validators.required),
            carerRelationshipNotes: this.formBuilder.control<string>(null, this.validateCarerRelation()),
        });
        this.carerGroup.get('carerRelationship')?.valueChanges.subscribe(value => {
            this.shownCarerRelationshipNotes = value == 7 // Other (id = 7)
            if (!this.shownCarerRelationshipNotes) {
                this.carerGroup.get('carerRelationshipNotes').setValue("")
            }
        });
    }

    private validateCarerRelation(): ValidatorFn {
        return (control: FormControl): ValidationErrors | null => {
            if (this.carerGroup?.get("carerRelationship").value == 7 && (control?.value == null || control?.value == "")) {
                return {required: true}
            }
            return null
        };
    }

    private validatePatientEmail(): ValidatorFn {
        return (control: FormControl): ValidationErrors | null => {
            if (!this.patientGroup?.get("patientEmailUnknown").value && (control?.value == null || control?.value == "")) {
                return {required: true}
            }
            return null
        };
    }

    private validateCarerEmail(): ValidatorFn {
        return (control: FormControl): ValidationErrors | null => {
            if (!this.carerGroup?.get("carerEmailUnknown").value && (control?.value == null || control?.value == "")) {
                return {required: true}
            }
            return null
        };
    }

    calculateAge(birthDate: string): number | null {
        return calculateAge(birthDate);
    }
    processForm(draft: boolean) {
        if (this.form.valid) {
            const value = this.form.value
            if (this.patient) {
                this.updatePatient(value, draft)
            } else {
                this.createPatient(value, draft)
            }
        } else {
            this.toastr.error('Please provide a value for the required fields');
            this.form.markAllAsTouched()
        }
    }

    private createPatient(value, draft: boolean) {
        this.patientsService.create({
            departmentId: Number(this.departmentId),
            licenseId: Number(this.licenseId),
            type: this.typeOfUser,
            clinicianId: this.clinicianId ?? undefined,
            isTest: this.isTest === true,
            firstName: value.patient?.firstName,
            lastName: value.patient?.lastName,
            dateOfBirth: value.patient?.dateOfBirth,
            age: value.patient?.age,
            sexAtBirth: value.patient?.sexAtBirth,
            phoneNumber: value.patient?.phoneNumber.e164Number,
            email: value.patient?.email,
            nhsNumber: value.patient?.nhsNumber,
            weight: value.patient?.weight,
            height: value.patient?.height,

            carerFirstName: value.carer?.carerFirstName,
            carerLastName: value.carer?.carerLastName,
            carerPhoneNumber: value.carer?.carerPhoneNumber?.e164Number,
            carerEmail: value.carer?.carerEmail,
            carerRelationshipId: Number(value.carer?.carerRelationship),
            carerRelationshipNotes: value.carer?.carerRelationshipNotes,
        }).subscribe({
            next: (response) => {
                this.toastr.success('User created successfully', 'Success');
                this.output.emit({patientId: response.id, draft: draft})
            },
            error: (error) => {
                this.toastr.error(error.error.exception, error.error.error);
            }
        })
    }

    private updatePatient(value, draft: boolean) {
        const isPatient = (this.typeOfUser === 'Patient' || this.typeOfUser === 'Patient and carer') && this.isUnmasked;
        const isCarer = (this.typeOfUser === 'Carer' || this.typeOfUser === 'Patient and carer') && this.isCarerUnmasked;

        this.patientsService.update(this.patient.id, {
            wizardStep: draft? 1 : 2,
            ...(isPatient ? {
                firstName: value.patient?.firstName,
                lastName: value.patient?.lastName,
                dateOfBirth: value.patient?.dateOfBirth,
                age: value.patient?.age,
                sexAtBirth: value.patient?.sexAtBirth,
                phoneNumber: value.patient?.phoneNumber?.e164Number,
                email: value.patient?.email,
                nhsNumber: value.patient?.nhsNumber,
                weight: value.patient?.weight,
                height: value.patient?.height,
            } : {}),
            ...(isCarer ? {
                carerFirstName: value.carer?.carerFirstName,
                carerLastName: value.carer?.carerLastName,
                carerPhoneNumber: value.carer?.carerPhoneNumber?.e164Number,
                carerEmail: value.carer?.carerEmail,
                carerRelationshipId: Number(value.carer?.carerRelationship),
                carerRelationshipNotes: value.carer?.carerRelationshipNotes,
            } : {}),
        }).subscribe({
            next: (response) => {
                this.toastr.success('User updated successfully', 'Success');
                this.output.emit({patientId: response.id, draft: draft})
            },
            error: (error) => {
                this.toastr.error(error.error.exception, error.error.error);
            }
        })
    }

    openPicker(input: HTMLInputElement) {
        if (input.showPicker) {
            input.showPicker();
        } else {
            input.focus();
        }
    }

    patientEmailUnknownChange() {
        if (this.patientGroup.get("patientEmailUnknown").value) {
            this.patientGroup.get('email').setValue("")
            this.patientGroup.get('email').disable()
        } else {
            this.patientGroup.get('email').enable()
        }
    }

    carerEmailUnknownChange() {
        if (this.carerGroup.get("carerEmailUnknown").value) {
            this.carerGroup.get('carerEmail').setValue("")
            this.carerGroup.get('carerEmail').disable()
        } else {
            this.carerGroup.get('carerEmail').enable()
        }
    }
}
